import { createFileRoute } from "@tanstack/react-router";
import { memo, useEffect } from "react";
import ViewmodelWrapper from "@/src/presentation/components/viewmodel-wrapper";
import ViewmodelConsumer from "@/src/presentation/components/viewmodel-consumer";
import PageLayout from "@/src/presentation/components/page-layout";
import useInject from "@/src/presentation/hooks/useInject";
import DeviceInfoViewModel from "@/src/presentation/view-models/device-info-viewmodel";
import ChartViewModel from "@/src/presentation/view-models/chart-viewmodel";
import TotalFlowViewModel from "@/src/presentation/view-models/total-flow-viewmodel";
import DeviceDetailTabs from "./_components/device-detail-tabs";
import MeterInfoCard from "./_components/meter-info-card";
import TotalFlowCard from "./_components/total-flow-card";
import MeterInfoCardSkeleton from "./_components/meter-info-card-skeleton";
import TotalFlowCardSkeleton from "./_components/total-flow-card-skeleton";
import DeviceDetailTabsSkeleton from "./_components/device-detail-tabs-skeleton";

const Initial = memo(() => {
  const deviceInfoVm = useInject(DeviceInfoViewModel);
  const chartVm = useInject(ChartViewModel);
  const totalFlowVm = useInject(TotalFlowViewModel);
  const { id } = Route.useParams();

  useEffect(() => {
    if (!id) return;
    const numericId = Number(id);
    deviceInfoVm.loadDeviceWithMeasurements(numericId).then((result) => {
      if (result?.totalFlowMeasurement) {
        totalFlowVm.setTotalFlow(result.totalFlowMeasurement);
      }
      if (deviceInfoVm.deviceInfo?.id) {
        chartVm.loadIntervalFlowMeasurement(deviceInfoVm.deviceInfo.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, deviceInfoVm.__currentVersion]);

  return null;
});

function DeviceDetailPage() {
  return (
    <ViewmodelWrapper types={[DeviceInfoViewModel, ChartViewModel, TotalFlowViewModel]}>
      <Initial />
      <PageLayout
        as="main"
        title="Device Dashboard"
        description="Monitor device information and real-time water consumption"
        className="min-h-screen py-4 md:py-8"
      >
        <div className="grid gap-6 md:cols-2 md:grid-cols-2 lg:grid-cols-2">
          <ViewmodelConsumer type={DeviceInfoViewModel} observe>
            {(model) => (model.isInitialLoad ? <MeterInfoCardSkeleton /> : <MeterInfoCard />)}
          </ViewmodelConsumer>
          <ViewmodelConsumer type={DeviceInfoViewModel} observe>
            {(deviceInfoVm) =>
              deviceInfoVm.isInitialLoad ? <TotalFlowCardSkeleton /> : <TotalFlowCard />
            }
          </ViewmodelConsumer>
        </div>
        <ViewmodelConsumer type={DeviceInfoViewModel} observe>
          {(model) => (model.isInitialLoad ? <DeviceDetailTabsSkeleton /> : <DeviceDetailTabs />)}
        </ViewmodelConsumer>
      </PageLayout>
    </ViewmodelWrapper>
  );
}

export const Route = createFileRoute("/_authenticated/dashboard/devices/$id/")({
  component: DeviceDetailPage,
});
